import { Clock, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip } from "recharts";

export const isPending = (v) => !v || String(v).includes("[");

export const KpiTile = ({ m, dark }) => {
  const pending = isPending(m.value);
  return (
    <div className={`rounded-2xl p-4 ${dark ? "bg-white/5 ring-1 ring-white/10" : "bg-[#F5F0FF]"}`}>
      <p className={`text-[11px] font-bold uppercase tracking-widest ${dark ? "text-violet-200" : "text-[#581C87]"}`}>{m.label}</p>
      {pending ? (
        <p className={`mt-2 flex items-center gap-1.5 text-xs font-semibold ${dark ? "text-violet-200" : "text-slate-500"}`}>
          <Clock className="h-3.5 w-3.5" /> Awaiting verified figure
        </p>
      ) : (
        <p className={`mt-1 text-2xl font-extrabold ${dark ? "text-white" : "text-[#13052A]"}`}>{m.value}</p>
      )}
    </div>
  );
};

export const MiniChart = ({ data, height = 120, dark }) => {
  if (!Array.isArray(data) || !data.length) {
    return (
      <div style={{ height }} className={`flex items-center justify-center rounded-2xl border border-dashed text-xs font-semibold ${dark ? "border-white/15 text-violet-200" : "border-violet-300 text-slate-500"}`}>
        Performance chart appears once figures are approved
      </div>
    );
  }
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
          <defs>
            <linearGradient id="cs" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#7C3AED" stopOpacity={0.5} />
              <stop offset="1" stopColor="#7C3AED" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: dark ? "#C4B5FD" : "#64748B" }} axisLine={false} tickLine={false} />
          <Tooltip />
          <Area type="monotone" dataKey="value" stroke="#7C3AED" strokeWidth={2.5} fill="url(#cs)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const CaseCard = ({ c, i = 0 }) => {
  const Wrap = c.page ? Link : "div";
  return (
  <Wrap {...(c.page ? { to: `/case-studies/${c.slug}` } : {})} data-testid={`case-card-${c.slug}`}
    className="group relative flex h-full flex-col overflow-hidden rounded-[28px] border border-white/10 bg-[#1B0938] p-6 text-white transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_40px_80px_-40px_rgba(124,58,237,.9)] sm:p-7">
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-violet-300" data-testid={`case-label-${c.slug}`}>
        <span className="pulse-dot h-1.5 w-1.5 shrink-0 rounded-full bg-[#60A5FA]" /> {c.card_label || (c.channels || []).join(" · ") || c.category}
      </span>
    </div>
    <h3 className="mt-6 text-2xl font-extrabold">{c.client}</h3>
    <p className="mt-1 text-sm text-violet-200">{c.industry}</p>
    {c.highlight && (
      <p className="mt-6 text-2xl font-extrabold leading-tight text-white sm:text-[28px]" data-testid={`case-highlight-${c.slug}`}>
        <span className="bg-gradient-to-r from-[#C4B5FD] to-[#60A5FA] bg-clip-text text-transparent">{c.highlight}</span>
      </p>
    )}
    {c.highlight_note && <p className="mt-2 text-sm leading-relaxed text-violet-100">{c.highlight_note}</p>}
    {!c.highlight && (
      <div className="mt-6 grid grid-cols-2 gap-2">
        {(c.metrics || []).slice(0, 2).map((m) => <KpiTile key={m.label} m={m} dark />)}
      </div>
    )}
    {Array.isArray(c.chart) && c.chart.length > 0 && <div className="mt-3"><MiniChart data={c.chart} height={96} dark /></div>}
    {c.page && (
      <span className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-bold text-[#FF8A65]">{c.card_cta || "Read case study"} <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></span>
    )}
  </Wrap>
  );
};
